import { useCallback, useEffect, useRef, useState } from 'react';
import { auth } from '../firebaseConfig';
import { authenticatePublishingWallet } from '../utils/digitalAssetsClient';
import { callNotifications } from '../utils/notificationsClient';

const initialQuery = () => ({ category: 'all', includeDismissed: false, page: 0, cursors: [null] });
const empty = { items: [], cursor: null, canHandle: false, loading: false, error: '' };

export function useNotifications(wallet) {
    const uid = wallet.publicKey?.toBase58?.() || '';
    const [session, setSession] = useState(null);
    const [query, setQuery] = useState(initialQuery);
    const [result, setResult] = useState({ ...empty, scope: '' });
    const [busy, setBusy] = useState('');
    const busyRef = useRef(false);
    const request = useRef(0);
    const mounted = useRef(true);
    const ready = wallet.connected && uid && session?.uid === uid && session.verified;
    const scope = JSON.stringify([uid, query]);
    const current = useRef({});
    current.current = { uid, scope, wallet, ready };
    const isCurrent = useCallback(capture => mounted.current && current.current.scope === capture.scope &&
        current.current.uid === capture.uid && current.current.wallet.connected &&
        current.current.wallet.publicKey?.toBase58?.() === capture.uid, []);

    useEffect(() => {
        mounted.current = true;
        let active = true;
        let generation = 0;
        const unsubscribe = auth.onIdTokenChanged(async user => {
            const version = ++generation;
            // Hide the previous session immediately while claims are checked.
            setSession(null);
            try {
                const token = user ? await user.getIdTokenResult() : null;
                if (active && version === generation) setSession({ uid: user?.uid || '', verified: token?.claims?.walletVerified === true });
            } catch {
                if (active && version === generation) setSession({ uid: '', verified: false });
            }
        });
        return () => { active = false; mounted.current = false; unsubscribe(); };
    }, []);

    useEffect(() => {
        setQuery(initialQuery());
        setBusy(''); busyRef.current = false;
    }, [uid]);

    const load = useCallback(async () => {
        const capture = { ...current.current };
        const version = ++request.current;
        if (!ready) { setResult({ ...empty, scope }); return; }
        setResult({ ...empty, scope, loading: true });
        try {
            const page = await callNotifications(capture.wallet, { action: 'list', category: query.category,
                includeDismissed: query.includeDismissed, cursor: query.cursors[query.page] }, { isCurrent: () => isCurrent(capture) && current.current.ready });
            if (version === request.current && isCurrent(capture) && current.current.ready) setResult({ ...empty, ...page, scope });
        } catch (error) {
            if (version === request.current && isCurrent(capture)) setResult({ ...empty, scope, error: error.message || 'Unable to load notifications.' });
        }
    }, [ready, scope, query, isCurrent]);
    useEffect(() => { load(); }, [load]);

    const signIn = async () => {
        if (busyRef.current) return;
        const capture = { ...current.current };
        busyRef.current = true; setBusy('sign-in');
        setResult(previous => ({ ...previous, error: '' }));
        try {
            await authenticatePublishingWallet(capture.wallet, { isCurrent: () => isCurrent(capture) });
            if (isCurrent(capture)) setSession({ uid: capture.uid, verified: true });
        }
        catch (error) {
            if (isCurrent(capture)) setResult({ ...empty, scope, error: error.message || 'Unable to sign in.' });
        } finally {
            if (isCurrent(capture)) { busyRef.current = false; setBusy(''); }
        }
    };
    const act = async (item, state, shared = false) => {
        if (busyRef.current || !ready) return;
        const capture = { ...current.current };
        busyRef.current = true; setBusy(item.id);
        setResult(previous => ({ ...previous, error: '' }));
        try {
            const change = await callNotifications(capture.wallet, { action: shared ? 'handle' : 'updateState', id: item.id, state,
                ...(shared ? { expectedVersion: item.handling.version } : {}) }, { isCurrent: () => isCurrent(capture) && current.current.ready });
            if (!isCurrent(capture) || !current.current.ready) return;
            setResult(previous => ({ ...previous, items: previous.items.map(row => row.id === item.id ? { ...row, ...change } : row)
                .filter(row => query.includeDismissed || !row.dismissedAt) }));
        } catch (error) {
            if (isCurrent(capture)) setResult(previous => ({ ...previous, error: error.message || 'Unable to update notification.' }));
        } finally {
            if (isCurrent(capture)) { busyRef.current = false; setBusy(''); }
        }
    };
    const view = result.scope === scope && ready ? result : { ...empty, error: result.scope === scope ? result.error : '' };
    return { ...view, ready: !!ready, checkingSession: session === null, busy, signIn,
        category: query.category, includeDismissed: query.includeDismissed, page: query.page + 1,
        refresh: load, act,
        setCategory: category => setQuery({ ...initialQuery(), category, includeDismissed: query.includeDismissed }),
        setIncludeDismissed: includeDismissed => setQuery({ ...initialQuery(), category: query.category, includeDismissed }),
        previous: () => setQuery(previous => ({ ...previous, page: Math.max(0, previous.page - 1) })),
        next: () => { if (view.cursor) setQuery(previous => ({ ...previous, page: previous.page + 1,
            cursors: [...previous.cursors.slice(0, previous.page + 1), view.cursor] })); },
    };
}
